import React from 'react';
import styled from 'styled-components';
import { collection, onSnapshot, updateDoc, doc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { logout } from '../../firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button } from '../ui/Button';
import { theme } from '../../theme';
import { FiMenu, FiBell, FiLogOut, FiUser } from 'react-icons/fi';
import { db } from '../../firebase/config';
import type { NotificationData } from '../../services/notificationService';
import { notificationCreatedAtMs } from '../../utils/notificationCreatedAt';

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  height: 70px;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  flex: 1;
  min-width: 0;
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.lg};
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    display: none;
  }
`;

const MenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.light.border.medium};
  background-color: ${theme.colors.white};
  color: ${theme.colors.dark.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
    border-color: ${theme.colors.light.border.dark};
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    display: flex;
  }
`;


const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.light.border.medium};
  background-color: ${theme.colors.white};
  color: ${theme.colors.light.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
    border-color: ${theme.colors.light.border.dark};
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: ${theme.borderRadius.full};
  background-color: ${theme.colors.accent.danger};
  color: ${theme.colors.white};
  font-size: 10px;
  font-weight: ${theme.typography.fontWeight.bold};
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding-left: ${theme.spacing[3]};
  border-left: 1px solid ${theme.colors.light.border.medium};
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  @media (max-width: ${theme.breakpoints.sm}) {
    display: none;
  }
`;

const UserName = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.light.text.primary};
`;

const UserRole = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
  text-transform: capitalize;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.primary[700]});
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.white};
  font-weight: ${theme.typography.fontWeight.bold};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + ${theme.spacing[2]});
  right: 0;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.lg};
  min-width: 200px;
  overflow: hidden;
  z-index: 1000;
`;

const NotificationWrap = styled.div`
  position: relative;
`;

const NotificationPanel = styled.div`
  position: absolute;
  top: calc(100% + ${theme.spacing[2]});
  right: 0;
  width: min(360px, calc(100vw - 24px));
  max-height: min(420px, 70vh);
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.lg};
  z-index: 1000;
  overflow: hidden;
`;

const NotificationPanelHeader = styled.div`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.light.border.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
`;

const NotificationPanelTitle = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.light.text.primary};
`;

const NotificationPanelMeta = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
`;

const NotificationScroll = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const NotificationEmpty = styled.div`
  padding: ${theme.spacing[6]} ${theme.spacing[4]};
  text-align: center;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const NotificationRow = styled.button<{ $unread?: boolean }>`
  width: 100%;
  text-align: left;
  border: none;
  border-bottom: 1px solid ${theme.colors.light.border.light};
  background: ${({ $unread }) => ($unread ? `${theme.colors.primary[500]}08` : theme.colors.white)};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NotificationRowTitle = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[1]};
`;

const NotificationRowBody = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const NotificationPanelFooter = styled.div`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-top: 1px solid ${theme.colors.light.border.light};
  background: ${theme.colors.light.background.secondary};
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  text-align: left;
  border: none;
  background: none;
  color: ${theme.colors.light.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
  }

  svg {
    font-size: ${theme.typography.fontSize.base};
  }
`;

interface DashboardHeaderProps {
  onMenuClick: () => void;
}

type NotificationRowData = NotificationData & { id: string };

function routeForNotification(companyId: string, n: Pick<NotificationData, 'type' | 'complainId'>): string {
  if (n.type === 'complain') {
    if (n.complainId) {
      return `/companies/${companyId}/complains?openComplain=${encodeURIComponent(n.complainId)}`;
    }
    return `/companies/${companyId}/complains`;
  }
  return '/dashboard';
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({ onMenuClick }) => {
  const { user, userData, companyData, staffData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [showNotifPanel, setShowNotifPanel] = React.useState(false);
  const [notifRows, setNotifRows] = React.useState<NotificationRowData[]>([]);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const notifRef = React.useRef<HTMLDivElement>(null);

  // Get page title based on route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/attendance')) return 'Guard Attendance';
    if (path.includes('/guards')) return 'Guards';
    if (path.includes('/deployment')) return 'Deployment';
    if (path.includes('/clients')) return 'Clients';
    if (path.includes('/staff')) return 'Staff';
    if (path.includes('/complains')) return 'Complains';
    if (path.includes('/notifications')) return 'Notifications';
    if (path.includes('/dashboard')) return 'Dashboard';
    if (path.includes('/reports')) return 'Reports';
    if (path.includes('/settings')) return 'Settings';
    return 'Dashboard';
  };

  React.useEffect(() => {
    const companyId = companyData?.id;
    if (!companyId) {
      setNotifRows([]);
      return undefined;
    }
    const col = collection(db, 'notifications', 'companies', companyId);
    const unsub = onSnapshot(
      col,
      (snap) => {
        const list: NotificationRowData[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as NotificationData),
        }));
        list.sort((a, b) => notificationCreatedAtMs(b.createdAt) - notificationCreatedAtMs(a.createdAt));
        setNotifRows(list);
      },
      (err) => {
        console.error(err);
        setNotifRows([]);
      }
    );
    return unsub;
  }, [companyData?.id]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const t = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(t)) {
        setShowDropdown(false);
      }
      if (notifRef.current && !notifRef.current.contains(t)) {
        setShowNotifPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
    }
  };

  const unreadCount = notifRows.filter((r) => !r.read).length;
  const previewRows = notifRows.slice(0, 8);

  const openNotificationFromPanel = (r: NotificationRowData) => {
    const companyId = companyData?.id;
    if (!companyId) return;
    const path = routeForNotification(companyId, r);
    void (async () => {
      if (!r.read) {
        try {
          await updateDoc(doc(db, 'notifications', 'companies', companyId, r.id), { read: true });
        } catch (e) {
          console.error(e);
        }
      }
      setShowNotifPanel(false);
      navigate(path);
    })();
  };

  const getInitials = (email: string | undefined, displayName: string | undefined) => {
    if (displayName) {
      return displayName
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return 'U';
  };

  return (
    <HeaderContainer>
      <LeftSection>
        <MenuButton onClick={onMenuClick}>
          <FiMenu size={20} />
        </MenuButton>
        <PageTitle>{getPageTitle()}</PageTitle>
      </LeftSection>

      <RightSection>
        {companyData?.id ? (
          <NotificationWrap ref={notifRef}>
            <IconButton
              type="button"
              title="Notifications"
              aria-label="Notifications"
              aria-expanded={showNotifPanel}
              onClick={() => setShowNotifPanel((open) => !open)}
            >
              <FiBell size={20} />
              {unreadCount > 0 ? (
                <NotificationBadge>{unreadCount > 99 ? '99+' : unreadCount}</NotificationBadge>
              ) : null}
            </IconButton>
            {showNotifPanel ? (
              <NotificationPanel role="dialog" aria-label="Notification list">
                <NotificationPanelHeader>
                  <div>
                    <NotificationPanelTitle>Notifications</NotificationPanelTitle>
                    <NotificationPanelMeta>
                      {unreadCount === 0 ? 'All caught up' : `${unreadCount} unread`}
                    </NotificationPanelMeta>
                  </div>
                </NotificationPanelHeader>
                <NotificationScroll>
                  {previewRows.length === 0 ? (
                    <NotificationEmpty>No notifications yet.</NotificationEmpty>
                  ) : (
                    previewRows.map((r) => (
                      <NotificationRow
                        key={r.id}
                        type="button"
                        $unread={!r.read}
                        onClick={() => openNotificationFromPanel(r)}
                      >
                        <NotificationRowTitle>{r.title}</NotificationRowTitle>
                        <NotificationRowBody>{r.body}</NotificationRowBody>
                      </NotificationRow>
                    ))
                  )}
                </NotificationScroll>
                <NotificationPanelFooter>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    style={{ width: '100%' }}
                    onClick={() => {
                      setShowNotifPanel(false);
                      navigate(`/companies/${companyData.id}/notifications`);
                    }}
                  >
                    View all
                  </Button>
                </NotificationPanelFooter>
              </NotificationPanel>
            ) : null}
          </NotificationWrap>
        ) : null}

        <UserSection>
          <UserInfo>
            <UserName>{user?.email || userData?.email || 'User'}</UserName>
            <UserRole>
              {staffData
                ? (staffData.department || staffData.departments?.[0] || staffData.role || 'staff')
                : (companyData ? 'admin' : (userData?.role || 'user'))}
            </UserRole>
          </UserInfo>
          <div style={{ position: 'relative' }} ref={dropdownRef}>
            <UserAvatar onClick={() => setShowDropdown(!showDropdown)}>
              {getInitials(user?.email || userData?.email, userData?.displayName || user?.displayName || undefined)}
            </UserAvatar>
            {showDropdown && (
              <DropdownMenu>
                <DropdownItem onClick={() => navigate('/profile')}>
                  <FiUser />
                  Profile
                </DropdownItem>
                <DropdownItem onClick={handleLogout}>
                  <FiLogOut />
                  Logout
                </DropdownItem>
              </DropdownMenu>
            )}
          </div>
        </UserSection>
      </RightSection>
    </HeaderContainer>
  );
};

export default DashboardHeader;

